/**
 * The workspace picker: which sector the console is working in, or the
 * platform (admin sector workspaces). It sits at the top of the sidebar,
 * shows the workspace with its customer count, and opens a menu to switch.
 * Switching keeps the page when the other sector has it.
 */
import { Fragment, type ElementType } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  AcademicCapIcon,
  CheckIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
  HomeModernIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline'
import { adminService } from '../services/admin'
import { switchTarget, type Sector } from '../sector/sector'

interface Look {
  icon: ElementType
  /** The icon tile: the workspace's colour, so it is clear where you are. */
  tile: string
}

const LOOKS: Record<string, Look> = {
  school: { icon: AcademicCapIcon, tile: 'bg-accent-soft text-accent' },
  rental: { icon: HomeModernIcon, tile: 'bg-highlight-soft text-highlight' },
}
const SECTOR_FALLBACK: Look = { icon: Squares2X2Icon, tile: 'bg-surface-alt text-ink-muted' }
const PLATFORM: Look = { icon: Cog6ToothIcon, tile: 'bg-surface-alt text-ink-muted' }

function sectorLook(code: string): Look {
  return LOOKS[code] ?? SECTOR_FALLBACK
}

function Tile({ look, size = 'md' }: { look: Look; size?: 'sm' | 'md' }) {
  const Icon = look.icon
  return (
    <span
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-chip',
        size === 'md' ? 'h-9 w-9' : 'h-7 w-7',
        look.tile,
      )}
      aria-hidden
    >
      <Icon className={size === 'md' ? 'h-5 w-5' : 'h-4 w-4'} />
    </span>
  )
}

const customers = (n: number | undefined) =>
  n === undefined ? '…' : `${n.toLocaleString('en-KE')} ${n === 1 ? 'customer' : 'customers'}`

export function SectorPicker({
  sectors,
  current,
  onPlatform,
  onNavigate,
}: {
  sectors: Sector[]
  /** The sector being worked in (the last one, on a platform page). */
  current: Sector | null
  onPlatform: boolean
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const counts = useQueries({
    queries: sectors.map((sector) => ({
      queryKey: ['tenants', sector.code],
      queryFn: () => adminService.listTenants(sector.code),
    })),
  })
  const countOf = (code: string) => counts[sectors.findIndex((s) => s.code === code)]?.data?.length

  const go = (to: string) => {
    navigate(to)
    onNavigate?.()
  }

  const shown = onPlatform || !current
    ? { look: PLATFORM, name: 'Platform', detail: 'Across every sector' }
    : { look: sectorLook(current.code), name: current.name, detail: customers(countOf(current.code)) }

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className="flex w-full items-center gap-3 rounded-card border border-hairline bg-surface p-2.5 text-left transition-colors duration-fast hover:bg-surface-alt focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-label={`Workspace: ${shown.name}. Switch workspace`}
      >
        <Tile look={shown.look} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-outfit text-body font-semibold text-ink">{shown.name}</span>
          <span className="block truncate font-inter text-caption text-ink-muted">{shown.detail}</span>
        </span>
        <ChevronUpDownIcon className="h-5 w-5 shrink-0 text-ink-faint" aria-hidden />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 right-0 z-50 mt-2 origin-top rounded-card border border-hairline bg-surface p-1.5 shadow-overlay focus:outline-none">
          <p className="px-2.5 pb-1 pt-1.5 font-outfit text-overline uppercase text-ink-faint">Sectors</p>
          {sectors.map((sector) => {
            const selected = !onPlatform && sector.code === current?.code
            return (
              <Menu.Item key={sector.code}>
                {({ active }) => (
                  <button
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    onClick={() => go(switchTarget(pathname, sector.code))}
                    className={clsx(
                      'flex w-full items-center gap-3 rounded-button px-2.5 py-2 text-left',
                      active && 'bg-surface-alt',
                    )}
                  >
                    <Tile look={sectorLook(sector.code)} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-inter text-body-sm font-medium text-ink">{sector.name}</span>
                      <span className="block font-inter text-caption text-ink-muted">{customers(countOf(sector.code))}</span>
                    </span>
                    {selected && <CheckIcon className="h-4 w-4 shrink-0 text-accent" aria-hidden />}
                  </button>
                )}
              </Menu.Item>
            )
          })}
          <div className="my-1.5 border-t border-hairline" />
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                aria-current={onPlatform ? 'true' : undefined}
                onClick={() => go('/platform/sectors')}
                className={clsx('flex w-full items-center gap-3 rounded-button px-2.5 py-2 text-left', active && 'bg-surface-alt')}
              >
                <Tile look={PLATFORM} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block font-inter text-body-sm font-medium text-ink">Platform</span>
                  <span className="block font-inter text-caption text-ink-muted">Sectors, roles, your account</span>
                </span>
                {onPlatform && <CheckIcon className="h-4 w-4 shrink-0 text-accent" aria-hidden />}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
